import { XIcon } from '@heroicons/react/solid';
import { Alert, Badge, Button, Card, Link, Select, Title } from '@yoast/ui-library';
import { format } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { useGetAnalysis, useGetAnalysisList, useGetBrand, useGetCurrentUser } from '../../api/generated/insights';
import { AIProviderEnum, AnalysisStatus } from '../../api/generated/insights.schemas';
import CardHeader from '../../components/CardHeader';
import Divider from '../../components/Divider';
import AnalysisDateSelect from '../../components/UI/AnalysisDateSelect';
import RouteGuardLoading from '../../components/RouteGuards/RouteGuardLoading';
import { useBrandAnalysisData, useBrandAnalysisMutations } from '../../contexts';
import { generateHashedLegalKey } from '../../utils/legalStorageUtils';
import { getAIProviderLabel } from '../../utils/stringUtils';
import { getLocalDateFromUTCTimestamp } from '../../utils/timeUtils';
import QueryCollapsibleList from './QueryCollapsibleList';

const AnalysisDetails = () => {
  const { brandId, analysisId, aiProvider } = useBrandAnalysisData();
  const { setAiProvider } = useBrandAnalysisMutations();
  const navigate = useNavigate();

  const { data: userResponse } = useGetCurrentUser({
    query: {
      select: ({ data }) => data,
    },
  });
  const user = userResponse?.data;

  const { data: analysisList, isLoading: isLoadingAnalysisList } =
    useGetAnalysisList(brandId, {
      query: {
        enabled: !!brandId,
        select: ({ data }) => data?.analyses,
      },
    });

  const { data: brandData, isLoading: isLoadingBrand } = useGetBrand(brandId, {
    query: {
      enabled: !!brandId,
      select: ({ data }) => data,
    },
  });

  const { data: analysisData, isLoading: isLoadingAnalysis } = useGetAnalysis(
    brandId,
    analysisId,
    {
      query: {
        enabled: !!brandId && !!analysisId,
        select: ({ data }) => data?.analysis,
      },
    },
  );

  const [showLegalAlert, setShowLegalAlert] = useState(false);

  useEffect(() => {
    const currentAnalysisId = analysisData?.id;
    const userId = user?.id;

    if (
      !brandId ||
      !currentAnalysisId ||
      !aiProvider ||
      !userId ||
      analysisData?.status !== AnalysisStatus.completed
    ) {
      setShowLegalAlert(false);
      return;
    }

    const hashedLegalKey = generateHashedLegalKey(
      userId,
      brandId,
      currentAnalysisId,
      aiProvider,
    );

    const isDismissed = window.localStorage.getItem(hashedLegalKey) !== null;
    setShowLegalAlert(!isDismissed);
  }, [brandId, analysisData?.id, analysisData?.status, aiProvider, user?.id]);

  const handleCloseLegalAlert = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const currentAnalysisId = analysisData?.id;
    const userId = user?.id;

    if (!brandId || !currentAnalysisId || !aiProvider || !userId) return;

    const secureKey = generateHashedLegalKey(
      userId,
      brandId,
      currentAnalysisId,
      aiProvider,
    );
    window.localStorage.setItem(secureKey, 'true');
    setShowLegalAlert(false);
  };

  const completedScans = useMemo(
    () => analysisData?.completedScans ?? [],
    [analysisData?.completedScans],
  );

  const currentScan = useMemo(
    () => completedScans.find((s) => String(s.aiProvider) === String(aiProvider)) ?? null,
    [completedScans, aiProvider],
  );

  const aiProviders =
    completedScans.length > 0
      ? completedScans.map((scan) => ({
          value: scan.aiProvider ?? '',
          label: getAIProviderLabel(scan.aiProvider),
        }))
      : [{ value: '', label: 'AI platform' }];

  // Initialize AI provider to first available completed scan if not set or unavailable
  useEffect(() => {
    if (completedScans.length > 0) {
      const availableProviders = completedScans.map((s) => s.aiProvider);
      if (!aiProvider || !availableProviders.includes(aiProvider)) {
        const firstProvider = completedScans[0].aiProvider;
        if (firstProvider) {
          setAiProvider(firstProvider as AIProviderEnum);
        }
      }
    }
  }, [completedScans, aiProvider, setAiProvider]);

  const isLoading = isLoadingBrand || isLoadingAnalysis || isLoadingAnalysisList;
  const hasData =
    analysisData && analysisData.status === AnalysisStatus.completed;

  if (isLoading) {
    return <RouteGuardLoading />;
  }

  if (!hasData) {
    return (
      <Card>
        <CardHeader title="Analysis details" />
        <Card.Content>
          <p>No completed analysis found. Please run an analysis first.</p>
        </Card.Content>
      </Card>
    );
  }

  const completedAt = getLocalDateFromUTCTimestamp(analysisData.completedAt);
  const formattedDate = completedAt
    ? format(completedAt, 'yyyy-MM-dd HH:mm:ss')
    : 'Unknown date';

  return (
    <Card className="!yst-overflow-visible">
      <Card.Content>
        {/* Title and subtitle */}
        <div className="yst-mb-6">
          <Title size="1">Analysis details</Title>
          <p className="yst-mt-1 yst-text-slate-500">
            Review the query results, citations, and sentiment data for this analysis.
          </p>
        </div>

        {/* Back button */}
        <Button
          as={RouterLink}
          className="yst-mb-2"
          variant="secondary"
          to={`/brands/${brandId}/analysis/${analysisId}`}
        >
          &larr; Back to analysis results
        </Button>
      </Card.Content>

      <Divider size={8} />

      <Card.Content className="!yst-mt-0">
        {/* Legal alert */}
        {showLegalAlert && (
          <Alert className="yst-mb-4">
            <div className="yst-flex yst-w-full">
              <div className="yst-max-w-xl yst-space-y-4">
                <p className="yst-mb-0">
                  Note: These insights are produced using AI and may contain
                  errors.
                </p>
              </div>
              <div className="yst-ml-auto">
                <Link onClick={handleCloseLegalAlert} variant="default">
                  <XIcon className="yst-h-4 yst-w-4" />
                </Link>
              </div>
            </div>
          </Alert>
        )}

        {/* Brand name + date */}
        <p className="!yst-mb-4">
          <span className="yst-text-lg yst-font-bold">{brandData?.name}</span>
          {' '}- Scan performed on:{' '}
          <Badge>{formattedDate}</Badge>
        </p>

        {/* Analysis date + AI platform dropdowns */}
        <div className="yst-flex yst-flex-wrap yst-gap-4">
          <div className="yst-flex-1 yst-min-w-[200px] yst-max-w-xs yst-space-y-2">
            <span className="yst-text-sm yst-font-medium yst-text-slate-600">Analysis date</span>
            <AnalysisDateSelect
              analyses={analysisList || []}
              selectedAnalysisId={analysisId}
              onAnalysisSelect={(newAnalysisId) => {
                navigate(
                  `/brands/${brandId}/analysis/${String(newAnalysisId)}/analysis-details`,
                );
              }}
              isLoading={isLoadingAnalysisList}
              disabled={
                isLoadingAnalysisList ||
                !analysisList ||
                analysisList.length < 1
              }
              id="analysis-date"
            />
          </div>
          <div className="yst-flex-1 yst-min-w-[200px] yst-max-w-xs yst-space-y-2">
            <span className="yst-text-sm yst-font-medium yst-text-slate-600">AI platform</span>
            <Select
              id="ai-platform-select"
              value={aiProvider ?? ''}
              options={
                isLoadingAnalysis
                  ? [{ value: aiProvider ?? '', label: 'Loading...' }]
                  : aiProviders
              }
              onChange={(newValue) => {
                setAiProvider(newValue as AIProviderEnum);
              }}
              disabled={isLoadingAnalysis || aiProviders.length < 2}
            />
          </div>
        </div>

        {currentScan?.id && (
          <QueryCollapsibleList
            brandId={brandId}
            analysisId={analysisId}
            scanId={currentScan.id}
          />
        )}
      </Card.Content>
    </Card>
  );
};

export default AnalysisDetails;
